import { TextArea } from 'components/core';
import { useEffect } from 'react';
import { useRecoilValue } from 'recoil';
import { currentDashboardState } from 'states';
import { UI_TEXT } from '../../../../../constants';

import styles from './DashboardTabs.module.css';
import { FieldTooltip } from 'molecules/fieldTooltip/FieldTooltip';

interface WidgetsProps {
  register: any;
  setValue: any;
  formState: any;
  isExisting?: boolean;
}

const handleChange = (setValue: any) => (e: any) => {
  e.preventDefault();
  setValue('widgetsConfig', e.target.value, { shouldValidate: true });
};

// ── Widget field documentation ────────────────────────────────────────────────
const WIDGET_FIELD_DOCS = [
  {
    field: 'id',
    label: 'id',
    description:
      "The unique visualization code of the chart to include in this dashboard. This must exactly match the Visualization Code set in the visualization's Info tab.",
    example: '"id": "aastrikaTotalUserReg"',
    note: 'Must match an existing visualization code exactly — otherwise the chart will not load.',
  },
  {
    field: 'name',
    label: 'name',
    description:
      'A human-readable label for this widget entry. Used for identification in the admin console — not shown to end users.',
    example: '"name": "Total Registered Students"',
  },
  {
    field: 'chartType',
    label: 'chartType',
    description:
      'The type of chart this widget displays. Must match the chart type configured in the visualization.',
    example: '"chartType": "metric"',
    values: 'metric, bar, horizontalBar, pie, line, table',
  },
  {
    field: 'order',
    label: 'order',
    description:
      'Controls the display order of widgets on the dashboard. Lower numbers appear first. Use this to arrange charts in the desired sequence.',
    example: '"order": 1',
  },
  {
    field: 'filter',
    label: 'filter',
    description:
      'An optional filter applied specifically to this widget, in addition to any global dashboard filters. Leave empty to use only the global filters.',
    example: '"filter": ""',
  },
  {
    field: 'headers',
    label: 'headers',
    description:
      'Column headers used for table-type charts. Leave as an empty array for non-table charts.',
    example: '"headers": []',
  },
  {
    field: 'code',
    label: 'code',
    description: 'An optional secondary code reference for this widget. Usually left empty.',
    example: '"code": ""',
  },
];

export const Widgets = ({ register, setValue, formState, isExisting = false }: WidgetsProps) => {
  const currentDashboard = useRecoilValue(currentDashboardState);

  useEffect(() => {
    if (!isExisting) {
      setValue('widgetsConfig', UI_TEXT.DASHBOARD_TEMPLATES.WIDGETS_CONFIG);
    }
  }, [currentDashboard]);
  const { errors } = formState;

  return (
    <div style={{ paddingBottom: '40px' }} className="col-sm-12 col-md-12 col-lg-8">
      <div className="col-sm-12 col-md-9 col-lg-9 mt-4 mb-3">
        {/* Field reference */}
        <div className={styles.fieldRefPanel} style={{ marginBottom: 20 }}>
          <div className={styles.fieldRefHeader}>
            <span className="material-icons" style={{ fontSize: 15, color: 'var(--primary-1)' }}>
              menu_book
            </span>
            <span className={styles.fieldRefTitle}>Widget field reference</span>
            <span className={styles.fieldRefSub}>
              Hover over a field name for help, or click to keep it open
            </span>
          </div>
          <div className={styles.fieldRefGrid}>
            {WIDGET_FIELD_DOCS.map((doc) => (
              <div key={doc.field} className={styles.fieldRefItem}>
                <div className={styles.fieldRefItemTop}>
                  <code className={styles.fieldRefCode}>{doc.label}</code>
                  <FieldTooltip
                    title={doc.label}
                    description={doc.description}
                    example={doc.example}
                    note={doc.note}
                  />
                </div>
                {(doc as any).values && (
                  <div className={styles.fieldRefValues}>
                    <span className={styles.fieldRefValuesLabel}>Values: </span>
                    {(doc as any).values}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Widgets textarea */}
        <div className="mb-4">
          <div className={styles.fieldLabelRow}>
            <span className={styles.fieldLabel}>Widgets configuration</span>
            <FieldTooltip
              title="Widgets configuration"
              description="A JSON array where each object represents one chart (widget) to include on this dashboard. Each widget references a visualization by its code and specifies display order and any widget-level filters. The field reference above explains each key."
              example='[{"id": "totalRegisteredUsers", "name": "Total Registered Professionals", "code": "", "chartType": "metric", "order": 1, "filter": "", "headers": []}]'
              note='The "id" field must exactly match an existing Visualization Code. Adding a widget here that does not have a matching visualization will result in an empty chart.'
            />
          </div>
          <div className={styles.fieldHint}>
            Each entry in this list adds one chart to the dashboard.
          </div>
          {!isExisting && (
            <div className={styles.jsonNote}>
              <span className="material-icons" style={{ fontSize: 16, flexShrink: 0 }}>
                info
              </span>
              <strong>
                {' '}
                New dashboard — the JSON below is a starter template. Edit it before saving.
              </strong>
            </div>
          )}
          <TextArea
            rows={10}
            showTitle={false}
            showDescription={false}
            title=""
            {...register('widgetsConfig')}
            className={`${errors.widgetsConfig ? 'is-invalid' : ''}`}
            onChange={handleChange(setValue)}
          />
          {errors.widgetsConfig && (
            <div className="invalid-feedback" style={{ display: 'block' }}>
              {errors.widgetsConfig.message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};